import { Injectable } from '@angular/core';
import { default as nacl } from 'tweetnacl';
import { environment as env } from '@env/environment';

/** Helper to check if an item is a standard JSON object */
function isPlainObject(item: any): boolean {
  return item !== null && typeof item === 'object' && !Array.isArray(item);
}

/** Recursively merges source into target */
function deepMerge(target: any, source: any): any {
  if (!isPlainObject(target) || !isPlainObject(source)) {
    return source;
  }
  const output = { ...target };
  for (const key of Object.keys(source)) {
    if (isPlainObject(source[key])) {
      output[key] = !(key in target) ? source[key] : deepMerge(target[key], source[key]);
    } else {
      output[key] = source[key];
    }
  }
  return output;
}

// UTF-8 & Hex Encoders (Native Web APIs, zero extra libraries needed)
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

@Injectable({
  providedIn: 'root',
})
export class StateManager {
  private readonly KEY = env.appId;
  private readonly ENABLE_ENCRYPTION = env.enableEncryption;
  private readonly derivedKey: Uint8Array;

  [key: string]: any;

  constructor() {
    // Derive a fixed 32-byte key required by secretbox using nacl.hash (SHA-512)
    const hashedSecret = nacl.hash(textEncoder.encode(this.KEY));
    this.derivedKey = hashedSecret.subarray(0, nacl.secretbox.keyLength); // First 32 bytes

    return new Proxy(this, {
      get(target: any, prop: string | symbol) {
        if (prop in target) {
          return target[prop];
        }
        if (typeof prop === 'string') {
          return target._getItem(prop);
        }
        return undefined;
      },

      set(target: any, prop: string | symbol, value: any) {
        if (typeof prop !== 'string') {
          return false;
        }
        if (prop in target || prop.startsWith('ng')) {
          target[prop] = value;
          return true;
        }
        target._saveItem(prop, value, true);
        return true;
      },

      deleteProperty(target: any, prop: string | symbol) {
        if (typeof prop === 'string' && !(prop in target)) {
          localStorage.removeItem(prop);
          return true;
        }
        return false;
      },
    });
  }

  /**
   * Internal SET logic: Handles XSalsa20-Poly1305 encryption via TweetNaCl
   */
  private _saveItem(key: string, newValue: any, merge: boolean): any {
    try {
      let finalValue = newValue;

      if (merge) {
        const existingValue = this._getItem(key);
        if (isPlainObject(existingValue) && isPlainObject(newValue)) {
          finalValue = deepMerge(existingValue, newValue);
        }
      }

      const jsonString = JSON.stringify(finalValue);
      if (!this.ENABLE_ENCRYPTION) {
        localStorage.setItem(key, jsonString);
        return finalValue;
      }
      const messageBytes = textEncoder.encode(jsonString);

      // Generate a fresh random 24-byte nonce for each encryption
      const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);

      // Encrypt
      const box = nacl.secretbox(messageBytes, nonce, this.derivedKey);

      // Pack nonce + box together in one array
      const combined = new Uint8Array(nonce.length + box.length);
      combined.set(nonce);
      combined.set(box, nonce.length);

      // Store in localStorage as Hex string
      localStorage.setItem(key, bytesToHex(combined));

      return finalValue;
    } catch (error) {
      console.error(`Error saving ${key} to local storage`, error);
      return null;
    }
  }

  /**
   * Internal GET logic: Handles XSalsa20-Poly1305 decryption via TweetNaCl
   */
  private _getItem(key: string): any {
    try {
      const hexString = localStorage.getItem(key);
      if (!hexString) {
        return null;
      }

      if (!this.ENABLE_ENCRYPTION) {
        return JSON.parse(hexString);
      }

      const combined = hexToBytes(hexString);

      // Ensure the payload is at least as long as the nonce
      if (combined.length < nacl.secretbox.nonceLength) {
        return null;
      }

      // Extract 24-byte nonce and encrypted ciphertext
      const nonce = combined.subarray(0, nacl.secretbox.nonceLength);
      const box = combined.subarray(nacl.secretbox.nonceLength);

      // Decrypt & Authenticate
      const decryptedBytes = nacl.secretbox.open(box, nonce, this.derivedKey);

      // If MAC verification fails or key is wrong, secretbox returns null
      if (!decryptedBytes) {
        console.warn(`Data integrity check failed for key: ${key}`);
        return null;
      }

      return JSON.parse(textDecoder.decode(decryptedBytes));
    } catch (error) {
      console.error(`Error reading ${key} from local storage`, error);
      return null;
    }
  }

  /**
   * Stores a new value in local storage.
   * By default, this method ensures you do not accidentally overwrite existing state.
   *
   * @param key - The local storage property name to create.
   * @param newValue - The value to encrypt and store.
   * @param failIfExisting - If true, throws an error if the key already exists. Defaults to true.
   * @throws {Error} If `failIfExisting` is true and the key is already populated.
   */
  public store(key: string, newValue: any, failIfExisting = true): void {
    if (failIfExisting && localStorage.getItem(key) !== null) {
      throw new Error(`State error: Key '${key}' already exists in storage. Use update() to modify it.`);
    }

    // Store as a strict overwrite since it's theoretically a new key
    this._saveItem(key, newValue, false);
  }

  /**
   * Updates an existing value in local storage.
   * Supports recursively deep-merging object properties without destroying sibling data.
   *
   * @param key - The local storage property name to update.
   * @param newValue - The new data to apply.
   * @param merge - If true, deep-merges objects. If false, completely overwrites the existing value. Defaults to true.
   */
  public update(key: string, newValue: any, merge = true): void {
    this._saveItem(key, newValue, merge);
  }

  /**
   * Removes a specific item from local storage.
   *
   * @param key - The local storage property name to remove.
   * @param failIfMissing - If true, throws an error if you attempt to delete a key that doesn't exist. Defaults to true.
   * @throws {Error} If `failIfMissing` is true and the key is not found in storage.
   */
  public delete(key: string, failIfMissing = true): void {
    if (failIfMissing && localStorage.getItem(key) === null) {
      throw new Error(`State error: Cannot delete '${key}' because it does not exist in storage.`);
    }
    localStorage.removeItem(key);
  }

  /**
   * Clears all items from localStorage
   */
  public clear(): void {
    localStorage.clear();
  }
}
